import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  OMP_PREVIEW_SOURCE_HOST,
  OMP_PREVIEW_SOURCE_PREVIEW,
  OmpHostMessageType,
  OmpPreviewMessage,
  OmpPreviewMessageType,
  OmpQueuedMessage,
} from '../_models';

const HELLO_INTERVAL_MS = 250;
const HELLO_TIMEOUT_MS = 10000;
const QUEUE_LIMIT = 20;

@Injectable()
export class OmpPreviewBridgeService implements OnDestroy {
  private frame: HTMLIFrameElement = null;
  private origin = '';
  private seq = 0;
  private helloTimer: any = null;
  private helloStarted = 0;
  private queue: OmpQueuedMessage[] = [];
  private listener: (event: MessageEvent) => void = null;
  private messagesSubject = new Subject<OmpPreviewMessage>();
  private readySubject = new BehaviorSubject<boolean>(false);
  private timedOutSubject = new Subject<void>();

  constructor(private zone: NgZone) {}

  get messages$(): Observable<OmpPreviewMessage> {
    return this.messagesSubject.asObservable();
  }

  get ready$(): Observable<boolean> {
    return this.readySubject.asObservable();
  }

  get timedOut$(): Observable<void> {
    return this.timedOutSubject.asObservable();
  }

  get isReady(): boolean {
    return this.readySubject.value;
  }

  get currentSeq(): number {
    return this.seq;
  }

  attach(frame: HTMLIFrameElement, origin: string) {
    this.detach();
    this.frame = frame;
    this.origin = origin;

    this.zone.runOutsideAngular(() => {
      this.listener = (event: MessageEvent) => this.onMessage(event);
      window.addEventListener('message', this.listener);
    });

    this.startHello();
  }

  detach() {
    if (this.listener) {
      window.removeEventListener('message', this.listener);
      this.listener = null;
    }
    this.stopHello();
    this.frame = null;
    this.origin = '';
    this.queue = [];
    this.readySubject.next(false);
  }

  send(type: OmpHostMessageType, payload: any) {
    if (this.frame && this.origin) {
      if (this.isReady) {
        this.post(type, payload);
      } else {
        this.enqueue(type, payload);
      }
    }
  }

  ngOnDestroy() {
    this.detach();
    this.messagesSubject.complete();
    this.readySubject.complete();
  }

  private post(type: OmpHostMessageType, payload: any) {
    this.seq++;
    const message = {
      source: OMP_PREVIEW_SOURCE_HOST,
      seq: this.seq,
      type: type,
      payload: payload,
    };
    if (this.frame && this.frame.contentWindow) {
      this.frame.contentWindow.postMessage(message, this.origin);
    }
  }

  private enqueue(type: OmpHostMessageType, payload: any) {
    this.queue = this.queue.filter((entry) => entry.type !== type);
    this.queue.push({ type: type, payload: payload });
    if (this.queue.length > QUEUE_LIMIT) {
      this.queue.shift();
    }
  }

  private flush() {
    const pending = this.queue;
    this.queue = [];
    pending.forEach((entry) => this.post(entry.type, entry.payload));
  }

  private startHello() {
    this.helloStarted = Date.now();
    this.zone.runOutsideAngular(() => {
      this.helloTimer = setInterval(() => {
        const expired = Date.now() - this.helloStarted > HELLO_TIMEOUT_MS;
        if (this.isReady || expired) {
          this.stopHello();
          if (expired && !this.isReady) {
            this.zone.run(() => this.timedOutSubject.next());
          }
        } else {
          this.post(OmpHostMessageType.Hello, {});
        }
      }, HELLO_INTERVAL_MS);
    });
  }

  private stopHello() {
    if (this.helloTimer) {
      clearInterval(this.helloTimer);
      this.helloTimer = null;
    }
  }

  private onMessage(event: MessageEvent) {
    const data = event.data as OmpPreviewMessage;
    const fromPreview =
      this.origin && event.origin === this.origin && data && data.source === OMP_PREVIEW_SOURCE_PREVIEW;
    if (fromPreview) {
      this.zone.run(() => {
        if (data.type === OmpPreviewMessageType.Ready || !this.isReady) {
          this.readySubject.next(true);
          this.stopHello();
          this.flush();
        }
        this.messagesSubject.next(data);
      });
    }
  }
}
